// Per-platform analytics page — shows every post we've published to the
// channel along with the latest Meta-side insights (impressions, reach,
// engagement). One page handles both Facebook and Instagram via a route
// param (/analytics/facebook OR /analytics/instagram); the layout is
// near-identical, the differences are which metric keys we render.
//
// State machine:
//   loading           → first GET /api/analytics/{channel}
//   not_connected     → user hasn't connected FB / IG yet
//   ready (empty)     → connected but nothing's been published to this
//                       platform yet
//   ready (with rows) → list of post cards
//
// Per-row "Refresh stats" button hits POST /api/analytics/{channel}/
// refresh/{post_id}, which re-fetches from Meta and writes a new
// snapshot row to post_insights. We swap the row's metrics in place
// without a full reload.
//
// Replaces the disconnect modal that used to fire from the sidebar
// FB/IG rows — disconnect now lives as a less-prominent button on this
// page (so the primary surface for connected platforms is the stats,
// not the disconnect action).

import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  type AnalyticsChannel,
  type AnalyticsListResponse,
  type AnalyticsPostRow,
  type AnalyticsMetrics,
  type FacebookMetrics,
  type InstagramMetrics,
  listAnalytics,
  refreshAnalytics,
  startFacebookOAuth,
  disconnectFacebook,
} from '@/lib/api';
import { useConfirm } from '@/hooks/useConfirm';
import { useFacebookConnection } from '@/hooks/useFacebookConnection';
import AppLayoutV2 from '@/components/feature/AppLayoutV2';

interface ChannelMeta {
  id: AnalyticsChannel;
  label: string;
  color: string;
  icon: string;
  /** Headline metric — the big number on each card. */
  primaryMetricLabel: string;
  /** Function to pick the headline metric off the normalized object. */
  primaryMetricFn: (m: AnalyticsMetrics | null) => number | null | undefined;
  /** Secondary metrics — small rows under the headline. */
  secondaryFn: (m: AnalyticsMetrics | null) => Array<{ label: string; value: number | null | undefined }>;
}

const CHANNEL_META: Record<AnalyticsChannel, ChannelMeta> = {
  facebook: {
    id: 'facebook',
    label: 'Facebook',
    color: '#1877F2',
    icon: 'ri-facebook-circle-line',
    // Meta REMOVED Page-post impressions + reach in Graph v23 (they error #100),
    // so we only surface the metrics that actually return: Clicks (hero),
    // Reactions, Engaged. No "—" columns for the App Review reviewer to misread.
    primaryMetricLabel: 'Clicks',
    primaryMetricFn: (m) => (m as FacebookMetrics | null)?.clicks,
    secondaryFn: (m) => {
      const f = m as FacebookMetrics | null;
      return [
        { label: 'Reactions', value: f?.reactions?.total },
        { label: 'Engaged', value: f?.engaged_users },
      ];
    },
  },
  instagram: {
    id: 'instagram',
    label: 'Instagram',
    color: '#E4405F',
    icon: 'ri-instagram-line',
    // Reach is still valid for IG media. Meta deprecated media `impressions`
    // (v22) → `views`; the backend maps `views` into the impressions field, so
    // we label it "Views" (the current metric name) rather than the retired one.
    primaryMetricLabel: 'Reach',
    primaryMetricFn: (m) => (m as InstagramMetrics | null)?.reach,
    secondaryFn: (m) => {
      const i = m as InstagramMetrics | null;
      return [
        { label: 'Views', value: i?.impressions },
        { label: 'Likes', value: i?.likes },
        { label: 'Comments', value: i?.comments },
        { label: 'Saves', value: i?.saves },
      ];
    },
  },
};

/** Pretty-print a metric value. null/undefined → "—". Big numbers get
 *  compact notation (1.2K, 4.5M) so cards don't run wide on viral posts. */
function fmtMetric(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  if (Math.abs(v) >= 1_000_000) return (v / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (Math.abs(v) >= 1_000) return (v / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(v);
}

function fmtRelativeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function AnalyticsPage() {
  const params = useParams<{ channel: string }>();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const channel = (params.channel === 'instagram' ? 'instagram' : 'facebook') as AnalyticsChannel;
  const meta = CHANNEL_META[channel];
  const { refresh: refreshFbStatus } = useFacebookConnection();

  const [data, setData] = useState<AnalyticsListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Tracks which row IDs are currently re-fetching from Meta so we can
  // show per-row spinners without re-rendering the whole list.
  const [refreshingIds, setRefreshingIds] = useState<Set<string>>(new Set());
  const [disconnecting, setDisconnecting] = useState(false);

  // Masonry column count — matches /gallery's breakpoints so the two
  // surfaces feel like the same product. Round-robin distribution into
  // N flex columns lets a 4:5 tile next to a 1:1 tile pack tightly
  // without the CSS-Grid staircase gap below the shorter one. See
  // CLAUDE.md decision #75 + gotcha #154 for why native masonry isn't
  // safe in 2026.
  const computeColumnCount = (): number => {
    if (typeof window === 'undefined') return 4;
    const w = window.innerWidth;
    if (w >= 2400) return 6;
    if (w >= 1920) return 5;        // FHD — smaller cards/thumbnails
    if (w >= 1536) return 4;        // 2xl
    if (w >= 1280) return 3;        // xl
    if (w >= 768) return 2;         // md
    return 1;                       // mobile
  };
  const [columnCount, setColumnCount] = useState<number>(computeColumnCount);
  useEffect(() => {
    let raf = 0;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const next = computeColumnCount();
        setColumnCount((prev) => (prev === next ? prev : next));
      });
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(raf);
    };
  }, []);

  // ---- Lifecycle ----
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The list response itself carries `connected` — no need for a
      // second round-trip to /api/auth/facebook here. The provider
      // owns the broader connection-status concern app-wide.
      const analytics = await listAnalytics(channel);
      setData(analytics);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load analytics');
    } finally {
      setLoading(false);
    }
  }, [channel]);

  useEffect(() => { void load(); }, [load]);

  // ---- Per-row refresh ----
  const handleRefresh = useCallback(async (postId: string) => {
    setRefreshingIds((prev) => new Set(prev).add(postId));
    try {
      const result = await refreshAnalytics(channel, postId);
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          rows: prev.rows.map((r) => {
            if (r.post_id !== postId) return r;
            return {
              ...r,
              metrics: result.metrics,
              metrics_fetched_at: result.fetched_at,
              fetch_error: result.error,
            };
          }),
        };
      });
    } catch (e) {
      // Swallow into per-row error string so one bad refresh doesn't
      // poison the whole list.
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          rows: prev.rows.map((r) =>
            r.post_id === postId
              ? { ...r, fetch_error: e instanceof Error ? e.message : 'Refresh failed' }
              : r,
          ),
        };
      });
    } finally {
      setRefreshingIds((prev) => {
        const next = new Set(prev);
        next.delete(postId);
        return next;
      });
    }
  }, [channel]);

  // ---- Connect / Disconnect ----
  const handleConnect = useCallback(async () => {
    try {
      const { redirectUrl } = await startFacebookOAuth();
      window.location.href = redirectUrl;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start Facebook OAuth');
    }
  }, []);

  const handleDisconnect = useCallback(async () => {
    const ok = await confirm({
      title: `Disconnect ${channel === 'facebook' ? 'Facebook' : 'Instagram'}?`,
      message:
        channel === 'facebook'
          ? 'You\'ll lose access to FB Page posting AND Instagram (since IG is linked through your FB Page). Reconnect anytime.'
          : 'Instagram is linked through Facebook. Disconnecting will disconnect BOTH FB and IG — re-link by reconnecting Facebook.',
      confirmLabel: 'Disconnect',
      cancelLabel: 'Keep connected',
      destructive: true,
    });
    if (!ok) return;
    setDisconnecting(true);
    try {
      await disconnectFacebook();
      // Refresh from the bottom — connection status drives the empty state.
      // Also bump the provider so the sidebar Connect rows update instantly.
      await Promise.all([load(), refreshFbStatus()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not disconnect');
    } finally {
      setDisconnecting(false);
    }
  }, [confirm, channel, load]);

  // ---- Render ----
  if (loading) {
    return (
      <AppLayoutV2>
        <div className="max-w-[1500px] mx-auto px-6 py-8">
          <div className="flex items-center gap-3 text-dark-muted">
            <i className="ri-loader-4-line animate-spin text-xl" />
            Loading {meta.label} analytics…
          </div>
        </div>
      </AppLayoutV2>
    );
  }

  if (error) {
    return (
      <AppLayoutV2>
        <div className="max-w-[1500px] mx-auto px-6 py-8">
          <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-red-700 text-sm">
            <i className="ri-error-warning-line mr-2" />
            {error}
          </div>
        </div>
      </AppLayoutV2>
    );
  }

  const rows = data?.rows || [];
  const connected = data?.connected ?? false;

  return (
    <AppLayoutV2>
    <div className="max-w-[1500px] mx-auto px-6 py-8 space-y-6">
      {/* ---------- Header ---------- */}
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-lg"
            style={{ backgroundColor: meta.color, boxShadow: `0 4px 16px ${meta.color}30` }}
          >
            <i className={`${meta.icon} text-2xl`} />
          </div>
          <div>
            <h1 className="font-display font-bold text-dark text-2xl leading-tight">
              {meta.label} Analytics
            </h1>
            <p className="text-sm text-dark-muted mt-0.5">
              {connected
                ? `${rows.length} post${rows.length === 1 ? '' : 's'} published through the app`
                : `Connect ${meta.label} to see stats on your published posts`}
            </p>
          </div>
        </div>
        {connected && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => void load()}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-surface-dark/40 text-dark text-sm font-bold hover:border-primary hover:text-primary transition-colors cursor-pointer"
              title="Reload the list"
            >
              <i className="ri-refresh-line" />
              Reload
            </button>
            <button
              onClick={() => void handleDisconnect()}
              disabled={disconnecting}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-red-200 text-red-600 text-sm font-bold hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"
            >
              {disconnecting ? (
                <><i className="ri-loader-4-line animate-spin" />Disconnecting…</>
              ) : (
                <><i className="ri-logout-box-r-line" />Disconnect</>
              )}
            </button>
          </div>
        )}
      </header>

      {/* ---------- Empty / Connect state ---------- */}
      {!connected && (
        <div className="rounded-2xl bg-white border border-surface-dark/40 p-8 text-center max-w-xl mx-auto">
          <div
            className="w-16 h-16 rounded-3xl mx-auto mb-4 flex items-center justify-center text-white"
            style={{ backgroundColor: meta.color }}
          >
            <i className={`${meta.icon} text-3xl`} />
          </div>
          <h2 className="font-display font-bold text-dark text-xl mb-2">
            Connect {meta.label}
          </h2>
          <p className="text-sm text-dark-muted mb-5 leading-relaxed">
            {channel === 'instagram'
              ? 'Instagram is linked through your Facebook Page. Connect Facebook + we\'ll discover the IG Business Account linked to it.'
              : 'Connect your Facebook Page to post from this app and see engagement stats on every post.'}
          </p>
          <button
            onClick={() => void handleConnect()}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-sm font-bold shadow-md cursor-pointer"
            style={{ backgroundColor: meta.color }}
          >
            <i className="ri-link" />
            Connect Facebook
          </button>
        </div>
      )}

      {/* ---------- Empty state (connected, no posts) ---------- */}
      {connected && rows.length === 0 && (
        <div className="rounded-2xl bg-white border border-surface-dark/40 p-8 text-center max-w-xl mx-auto">
          <i className="ri-image-line text-4xl text-dark-muted/40 mb-3 block" />
          <h2 className="font-display font-bold text-dark text-lg mb-2">
            Nothing posted to {meta.label} yet
          </h2>
          <p className="text-sm text-dark-muted mb-5">
            Publish a creative through the app and stats will land here within a few hours.
          </p>
          <Link
            to="/gallery"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white text-sm font-bold shadow-md hover:bg-primary-hover cursor-pointer"
          >
            <i className="ri-gallery-line" />
            Open My Creatives
          </Link>
        </div>
      )}

      {/* ---------- Posts grid — masonry via N flex columns + round-robin
              distribution, same pattern as /gallery. Lets 4:5 cards next
              to 1:1 cards pack without the CSS-Grid "staircase" gap. ----- */}
      {connected && rows.length > 0 && (() => {
        const columns: AnalyticsPostRow[][] = Array.from(
          { length: columnCount }, () => [],
        );
        rows.forEach((row, idx) => {
          columns[idx % columnCount].push(row);
        });
        return (
          <div className="flex gap-4 items-start">
            {columns.map((col, colIdx) => (
              <div
                key={`col-${colIdx}`}
                className="flex-1 min-w-0 flex flex-col gap-4"
              >
                {col.map((row) => (
                  <AnalyticsCard
                    key={row.post_id}
                    row={row}
                    meta={meta}
                    refreshing={refreshingIds.has(row.post_id)}
                    onRefresh={() => void handleRefresh(row.post_id)}
                    onOpen={() => navigate(`/creatives/${row.post_id}`)}
                  />
                ))}
              </div>
            ))}
          </div>
        );
      })()}
    </div>
    </AppLayoutV2>
  );
}

interface AnalyticsCardProps {
  row: AnalyticsPostRow;
  meta: ChannelMeta;
  refreshing: boolean;
  onRefresh: () => void;
  onOpen: () => void;
}

function AnalyticsCard({ row, meta, refreshing, onRefresh, onOpen }: AnalyticsCardProps) {
  const primary = meta.primaryMetricFn(row.metrics);
  const secondary = meta.secondaryFn(row.metrics);
  const hasMetrics = row.metrics_fetched_at != null;

  return (
    <div className="rounded-2xl bg-white border border-surface-dark/40 overflow-hidden hover:shadow-md hover:shadow-primary/10 hover:border-primary/40 transition-all">
      {/* Image */}
      <button
        onClick={onOpen}
        className={`block w-full ${row.aspect_ratio === '4:5' ? 'aspect-[4/5]' : 'aspect-square'} bg-surface relative overflow-hidden cursor-pointer`}
        title="Open post"
      >
        {row.image_url ? (
          <img
            src={row.image_url}
            alt={row.title || 'Post'}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <i className="ri-image-line text-3xl text-dark-muted/40" />
          </div>
        )}
        {/* Channel chip */}
        <span
          className="absolute top-2 left-2 px-2 py-0.5 rounded-md text-white text-[11px] font-bold shadow-md inline-flex items-center gap-1"
          style={{ backgroundColor: meta.color }}
        >
          <i className={meta.icon} />
          {meta.label}
        </span>
      </button>

      {/* Body */}
      <div className="p-4 space-y-3">
        {row.title && (
          <div className="text-sm font-bold text-dark line-clamp-1" title={row.title}>
            {row.title}
          </div>
        )}

        {/* Primary metric — big number */}
        <div className="flex items-end justify-between gap-3 pt-1">
          <div>
            <div className="text-3xl font-display font-bold text-dark leading-none">
              {fmtMetric(primary)}
            </div>
            <div className="text-[11px] uppercase tracking-wider text-dark-muted font-bold mt-0.5">
              {meta.primaryMetricLabel}
            </div>
          </div>
          <button
            onClick={onRefresh}
            disabled={refreshing}
            title="Re-fetch from Meta now"
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-surface text-dark-muted text-[11px] font-bold border border-surface-dark/30 hover:border-primary hover:text-primary transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {refreshing ? (
              <><i className="ri-loader-4-line animate-spin" />Refreshing…</>
            ) : (
              <><i className="ri-refresh-line" />Refresh</>
            )}
          </button>
        </div>

        {/* Secondary metrics */}
        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-surface-dark/20">
          {secondary.map((m) => (
            <div key={m.label}>
              <div className="text-base font-bold text-dark leading-none">
                {fmtMetric(m.value)}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-dark-muted font-semibold mt-0.5">
                {m.label}
              </div>
            </div>
          ))}
        </div>

        {/* Last fetched + error */}
        <div className="pt-2 border-t border-surface-dark/20 text-[11px] text-dark-muted flex items-center justify-between">
          <span>
            {hasMetrics ? `Updated ${fmtRelativeTime(row.metrics_fetched_at)}` : 'No stats yet'}
          </span>
          <span title={row.published_at}>Posted {fmtRelativeTime(row.published_at)}</span>
        </div>

        {row.fetch_error && (
          <div
            className="px-2 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-[11px]"
            title={row.fetch_error}
          >
            <i className="ri-error-warning-line mr-1" />
            {row.fetch_error.length > 80 ? row.fetch_error.slice(0, 80) + '…' : row.fetch_error}
          </div>
        )}
      </div>
    </div>
  );
}
